import { useTheme } from "@emotion/react";
import { faInfoCircle, faTrash } from "@fortawesome/pro-light-svg-icons";
import { faColon, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { useStore } from "@tanstack/react-form";
import {
  DataSource,
  Operator,
  ProviderType,
} from "@ternary/api-lib/analytics/enums";
import { billingFocusSchema } from "@ternary/api-lib/analytics/schemas/billingFOCUS";
import { Dimension } from "@ternary/api-lib/analytics/ui/types";
import { DimensionPreferenceEntity } from "@ternary/api-lib/core/types";
import { ReallocationType } from "@ternary/api-lib/core/types/BillingRule";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import { Tooltip } from "@ternary/api-lib/ui-lib/components/Tooltip";
import Divider from "@ternary/web-ui-lib/components/Divider";
import { sortBy } from "lodash";
import React from "react";
import { z } from "zod";
import { DimensionValuesMap } from "../../../../api/analytics/useGetDimensionValues";
import { operatorOptions } from "../../../../constants";
import DualListbox from "../../../../ui-lib/components/DualListBox";
import { FormField } from "../../../../ui-lib/components/Form";
import NumberInput from "../../../../ui-lib/components/NumberInput";
import Select from "../../../../ui-lib/components/Select";
import SelectCheckbox from "../../../../ui-lib/components/SelectCheckbox";
import { groupOptionsByPreferences } from "../../../../utils/groupOptionsByPreferences";
import copyText from "../../copyText";
import { BillingRuleForm } from "./BillingRuleForm";
import { FilterSection } from "./FilterSection";
import { filterSchema, Form, staticTargetSchema } from "./types";

const validateKeyValuePair = (key: string, value: string) => {
  const bothEmpty = key.length === 0 && value.length === 0;
  const bothFilled = key.length > 0 && value.length > 0;
  return bothEmpty || bothFilled;
};

const measureOptions = [
  {
    label: billingFocusSchema.measures.billedCost.displayName,
    value: billingFocusSchema.measures.billedCost.schemaName,
  },
  {
    label: billingFocusSchema.measures.consumedQuantity.displayName,
    value: billingFocusSchema.measures.consumedQuantity.schemaName,
  },
  {
    label: billingFocusSchema.measures.effectiveCost.displayName,
    value: billingFocusSchema.measures.effectiveCost.schemaName,
  },
];

const reallocationTypeOptions = [
  ReallocationType.DYNAMIC,
  ReallocationType.STATIC,
].map((type) => ({
  label: copyText[`reallocationTypeOptionLabel_${type}`],
  value: type,
}));

interface Props {
  availableDimensions: Dimension[];
  dimensionPreferences: DimensionPreferenceEntity[];
  dimensionValues: DimensionValuesMap;
  form: Form;
  isLoadingDimensionPreferences: boolean;
  isLoadingDimensionValues: boolean;
  isLoadingSubAccountIDs: boolean;
  subAccountIDs: string[];
  onInteraction: (interaction: BillingRuleForm.Interaction) => void;
}

export function BillingRuleFormReallocateCosts(props: Props) {
  const theme = useTheme();

  const { form } = props;

  const formState = useStore(form.store, (state) => ({
    reallocationType: state.values.reallocationType,
  }));

  const dimensionOptions = sortBy(
    props.availableDimensions.map((availableDimension) => ({
      label: availableDimension.displayName,
      value: availableDimension.schemaName,
    })),
    "label"
  );

  const groupedFilterOptions = groupOptionsByPreferences(
    dimensionOptions,
    props.dimensionPreferences,
    DataSource.FOCUS_BILLING
  );

  const subAccountOptions = sortBy(
    props.subAccountIDs.map((subAccountID) => ({
      label: subAccountID,
      value: subAccountID,
    })),
    (option) => option.label.toLowerCase()
  );

  function renderStaticReallocationContent() {
    return (
      <Box padding={theme.space_md}>
        <Flex direction="column">
          <Text marginBottom={theme.space_xs}>
            {copyText.targetsSectionTitle}
          </Text>
          <form.Field
            name="targetsStatic"
            validators={{
              onChange: z.array(staticTargetSchema).min(1),
              onMount: z.array(staticTargetSchema).min(1),
            }}
            mode="array"
            children={(field) => {
              return (
                <>
                  {field.state.value.map((_, targetIndex) => (
                    <Flex
                      key={targetIndex}
                      direction="column"
                      gap={theme.space_sm}
                      marginBottom={theme.space_md}
                    >
                      <Flex
                        backgroundColor={theme.background_color}
                        border={`1px solid ${theme.border_color}`}
                        borderRadius={theme.borderRadius_2}
                        gap={theme.space_xs}
                        paddingHorizontal={theme.space_md}
                      >
                        <Flex
                          direction="column"
                          paddingVertical={theme.space_md}
                          width={150}
                        >
                          <form.Field
                            name={`targetsStatic[${targetIndex}].identifier.subAccountID`}
                            validators={{
                              onChange: z.string().nonempty(),
                              onMount: z.string().nonempty(),
                            }}
                            children={(subField) => {
                              const selectedOption = subAccountOptions.find(
                                (option) =>
                                  option.value === subField.state.value
                              );

                              return (
                                <FormField
                                  label="Sub Account ID"
                                  marginBottom={theme.space_md}
                                >
                                  <Select
                                    isLoading={props.isLoadingSubAccountIDs}
                                    isSearchable
                                    options={subAccountOptions}
                                    value={selectedOption}
                                    onChange={(option) => {
                                      if (option) {
                                        subField.handleChange(option.value);
                                      }
                                    }}
                                  />
                                </FormField>
                              );
                            }}
                          />
                          <form.Field
                            name={`targetsStatic[${targetIndex}].share`}
                            validators={{
                              onChange: z.number().refine(
                                () => {
                                  const allTargets = field.state.value;

                                  const totalShare = allTargets.reduce(
                                    (sum, target) => sum + target.share,
                                    0
                                  );

                                  return totalShare === 100;
                                },
                                { message: "Total share must equal 100%" }
                              ),
                              onMount: z.number().refine(
                                () => {
                                  const allTargets = field.state.value;

                                  const totalShare = allTargets.reduce(
                                    (sum, target) => sum + target.share,
                                    0
                                  );

                                  return totalShare === 100;
                                },
                                { message: "Total share must equal 100%" }
                              ),
                            }}
                            children={(subField) => (
                              <FormField
                                input={NumberInput}
                                formatter="percent-divided"
                                label="Share"
                                marginBottom={0}
                                value={subField.state.value}
                                onChange={(value) => {
                                  subField.handleChange(value);
                                }}
                              />
                            )}
                          />
                        </Flex>
                        <Divider
                          color={theme.border_color}
                          direction="vertical"
                          height={168}
                          margin={theme.space_xs}
                        />
                        <Flex flexGrow={1} paddingVertical={theme.space_md}>
                          <form.Field
                            name={`targetsStatic[${targetIndex}].identifier.labels`}
                            children={(subField) => (
                              <Flex direction="column" width="100%">
                                <Text>{copyText.labelsSectionTitle}</Text>
                                <Flex
                                  direction="column"
                                  gap={theme.space_xs}
                                  marginBottom={theme.space_xs}
                                >
                                  {subField.state.value.map(
                                    (label, labelIndex) => {
                                      const dimensionValues =
                                        props.dimensionValues[label.key] ?? [];

                                      const valueOptions = dimensionValues
                                        .sort()
                                        .map((value) => ({
                                          label: value,
                                          value,
                                        }));

                                      return (
                                        <Flex
                                          key={labelIndex}
                                          alignItems="center"
                                          gap={theme.space_xs}
                                        >
                                          <form.Field
                                            name={`targetsStatic[${targetIndex}].identifier.labels[${labelIndex}].key`}
                                            validators={{
                                              onChange: z
                                                .string()
                                                .refine(
                                                  (key) =>
                                                    validateKeyValuePair(
                                                      key,
                                                      label.value
                                                    ),
                                                  {
                                                    message:
                                                      "Both key and value must be filled or both must be empty",
                                                  }
                                                ),
                                            }}
                                            children={(subField) => {
                                              const selectedOption =
                                                dimensionOptions.find(
                                                  (option) =>
                                                    option.value ===
                                                    subField.state.value
                                                );

                                              return (
                                                <Box width={240}>
                                                  <Select
                                                    isSearchable
                                                    options={dimensionOptions}
                                                    value={selectedOption}
                                                    onChange={(option) => {
                                                      if (option) {
                                                        subField.handleChange(
                                                          option.value
                                                        );

                                                        props.onInteraction({
                                                          type: BillingRuleForm.INTERACTION_UPDATE_DIMENSIONS,
                                                          dimensionName:
                                                            option.value,
                                                        });
                                                      }
                                                    }}
                                                  />
                                                </Box>
                                              );
                                            }}
                                          />
                                          <Icon icon={faColon} />
                                          <form.Field
                                            name={`targetsStatic[${targetIndex}].identifier.labels[${labelIndex}].value`}
                                            validators={{
                                              onChange: z
                                                .string()
                                                .refine(
                                                  (value) =>
                                                    validateKeyValuePair(
                                                      label.key,
                                                      value
                                                    ),
                                                  {
                                                    message:
                                                      "Both key and value must be filled or both must be empty",
                                                  }
                                                ),
                                            }}
                                            children={(subField) => {
                                              const isLoading =
                                                subField.state.value.length > 0
                                                  ? false
                                                  : props.isLoadingDimensionValues;

                                              return (
                                                <Box width={240}>
                                                  <Select
                                                    isLoading={isLoading}
                                                    options={valueOptions}
                                                    onChange={(option) =>
                                                      option &&
                                                      subField.handleChange(
                                                        option.value
                                                      )
                                                    }
                                                  />
                                                </Box>
                                              );
                                            }}
                                          />
                                          <Button
                                            iconEnd={<Icon icon={faTrash} />}
                                            type="button"
                                            onClick={() => {
                                              subField.removeValue(labelIndex);
                                            }}
                                          />
                                        </Flex>
                                      );
                                    }
                                  )}
                                </Flex>
                                <Button
                                  iconStart={<Icon icon={faPlus} />}
                                  secondary
                                  size="tiny"
                                  type="button"
                                  width={110}
                                  onClick={() =>
                                    subField.pushValue({
                                      key: "",
                                      value: "",
                                    })
                                  }
                                >
                                  Add Label
                                </Button>
                              </Flex>
                            )}
                          />
                        </Flex>
                      </Flex>
                      <Flex justifyContent="flex-end">
                        <Button
                          size="small"
                          type="button"
                          onClick={() => field.removeValue(targetIndex)}
                        >
                          Remove Target
                        </Button>
                      </Flex>
                    </Flex>
                  ))}
                  <Button
                    iconStart={<Icon icon={faPlus} />}
                    size="tiny"
                    type="button"
                    width={110}
                    onClick={() =>
                      field.pushValue({
                        identifier: {
                          labels: [{ key: "", value: "" }],
                          subAccountID: "",
                        },
                        share: 0,
                      })
                    }
                  >
                    {copyText.addTargetButtonLabel}
                  </Button>
                </>
              );
            }}
          />
        </Flex>
      </Box>
    );
  }

  function renderDynamicReallocationContent() {
    return (
      <Box padding={theme.space_md}>
        <Flex gap={theme.space_xs} marginBottom={theme.space_lg}>
          <form.Field
            name="measure"
            validators={{
              onChange: z.string().nonempty(),
              onMount: z.string().nonempty(),
            }}
            children={(field) => {
              const selectedOption = measureOptions.find(
                (option) => option.value === field.state.value
              );

              return (
                <FormField label="Measure" marginBottom={0}>
                  <Select
                    options={measureOptions}
                    value={selectedOption}
                    onChange={(option) => {
                      if (option) {
                        field.handleChange(option.value);
                      }
                    }}
                  />
                </FormField>
              );
            }}
          />
          <form.Field
            name="minimumShare"
            validators={{
              onChange: z
                .number()
                .min(0)
                .max(100)
                .refine(
                  (value) => {
                    if (value === 0) return true;
                    // Check if number has more than 6 decimal places
                    const decimalPlaces =
                      value.toString().split(".")[1]?.length || 0;
                    return decimalPlaces <= 6;
                  },
                  { message: "Number must have at most 6 decimal places" }
                ),
              onMount: z
                .number()
                .min(0)
                .max(100)
                .refine(
                  (value) => {
                    if (value === 0) return true;
                    // Check if number has more than 6 decimal places
                    const decimalPlaces =
                      value.toString().split(".")[1]?.length || 0;
                    return decimalPlaces <= 6;
                  },
                  { message: "Number must have at most 6 decimal places" }
                ),
            }}
            children={(field) => {
              return (
                <FormField
                  input={NumberInput}
                  formatter="percent-divided"
                  label="Minimum Share"
                  marginBottom={0}
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                />
              );
            }}
          />
        </Flex>
        <form.Field
          name="usageGroupingDimensions"
          validators={{
            onChange: z.array(z.string()).min(1),
            onMount: z.array(z.string()).min(1),
          }}
          children={(field) => {
            return (
              <Box marginBottom={theme.space_md}>
                <DualListbox
                  label={
                    <Flex alignItems="center" gap={theme.space_xs}>
                      <Text>{copyText.weightingKeysSectionTitle}</Text>
                      <Icon color={"#535862"} icon={faInfoCircle} />
                    </Flex>
                  }
                  isLoading={props.isLoadingDimensionPreferences}
                  options={dimensionOptions}
                  selectedOptions={field.state.value}
                  onChange={(selectedOptions) => {
                    field.handleChange(selectedOptions);
                  }}
                />
              </Box>
            );
          }}
        />
        <Flex alignItems="center" gap={theme.space_xs}>
          <Text fontSize={theme.fontSize_base}>
            {copyText.filtersSectionLabel}
          </Text>
          <Icon color={"#535862"} icon={faInfoCircle} />
        </Flex>
        <form.Field
          name="filtersDynamic"
          mode="array"
          validators={{
            onChange: z.array(filterSchema),
            onMount: z.array(filterSchema),
          }}
          children={(field) => {
            return (
              <Flex direction="column" gap={theme.space_sm}>
                {field.state.value.map((filter, i) => {
                  const dimensionValues =
                    props.dimensionValues[filter.schemaName] ?? [];

                  const valueOptions = dimensionValues
                    .sort()
                    .map((value) => ({ label: value, value }));

                  return (
                    <Flex key={i} gap={theme.space_xs}>
                      <form.Field
                        name={`filtersDynamic[${i}].schemaName`}
                        validators={{
                          onChange: z.string().nonempty(),
                          onMount: z.string().nonempty(),
                        }}
                        children={(subField) => {
                          const selectedValue =
                            filter.schemaName.length > 0
                              ? {
                                  label: filter.displayName,
                                  value: filter.schemaName,
                                }
                              : null;

                          return (
                            <Select
                              isLoading={props.isLoadingDimensionPreferences}
                              hideSelectedOptions
                              options={groupedFilterOptions}
                              searchable
                              value={selectedValue}
                              onChange={(option) => {
                                if (option) {
                                  // First update the schemaName field to clear its validation
                                  subField.handleChange(option.value);

                                  // Then update the parent filter object
                                  field.replaceValue(i, {
                                    ...filter,
                                    displayName: option.label,
                                    schemaName: option.value,
                                    values: [],
                                  });

                                  props.onInteraction({
                                    type: BillingRuleForm.INTERACTION_UPDATE_DIMENSIONS,
                                    dimensionName: option.value,
                                  });
                                }
                              }}
                            />
                          );
                        }}
                      />
                      <form.Field
                        name={`filtersDynamic[${i}].operator`}
                        children={(subField) => {
                          const selectedOption = operatorOptions.find(
                            (option) => option.value === subField.state.value
                          );

                          return (
                            <Select
                              options={operatorOptions}
                              value={selectedOption}
                              onChange={(option) =>
                                option && subField.handleChange(option.value)
                              }
                            />
                          );
                        }}
                      />
                      <form.Subscribe
                        selector={(state) =>
                          state.values.filtersDynamic[i].operator
                        }
                        children={(operator) => {
                          if (
                            operator === Operator.NOT_SET ||
                            operator === Operator.SET
                          ) {
                            return null;
                          }

                          return (
                            <form.Field
                              name={`filtersDynamic[${i}].values`}
                              children={(subField) => {
                                const isLoading =
                                  subField.state.value &&
                                  subField.state.value.length > 0
                                    ? false
                                    : props.isLoadingDimensionValues;

                                return (
                                  <SelectCheckbox
                                    isCreatable={
                                      operator === Operator.CONTAINS ||
                                      operator === Operator.NOT_CONTAINS
                                    }
                                    isLoading={isLoading}
                                    options={valueOptions}
                                    selectedValues={filter.values ?? []}
                                    onChange={(values) =>
                                      subField.handleChange(values)
                                    }
                                  />
                                );
                              }}
                            />
                          );
                        }}
                      />
                    </Flex>
                  );
                })}
                <Button
                  iconStart={<Icon icon={faPlus} />}
                  size="tiny"
                  type="button"
                  width={100}
                  onClick={() =>
                    field.pushValue({
                      displayName: "",
                      operator: Operator.EQUALS,
                      providerType: ProviderType.FOCUS,
                      schemaName: "",
                      values: [],
                    })
                  }
                >
                  Add Filter
                </Button>
              </Flex>
            );
          }}
        />
      </Box>
    );
  }

  return (
    <>
      <Box marginBottom={theme.space_sm}>
        <Divider color={"#C2C8D0"} direction="vertical" minHeight={24} />
      </Box>
      <Flex direction="column" gap={theme.space_xs}>
        <Box>
          <Flex alignItems="center" gap={theme.space_xs}>
            <Text fontSize="18px">{copyText.costOriginSectionTitle}</Text>
            <Tooltip
              content={copyText.costOriginTooltipMessage}
              placement="bottom"
              width="250px"
            >
              <Icon color={"#535862"} icon={faInfoCircle} />
            </Tooltip>
          </Flex>
          <Text color="#7C8591">{copyText.costOriginSectionDescription}</Text>
        </Box>
        <FilterSection
          availableDimensions={props.availableDimensions}
          dimensionPreferences={props.dimensionPreferences}
          dimensionValues={props.dimensionValues}
          form={form}
          isLoadingDimensionPreferences={props.isLoadingDimensionPreferences}
          isLoadingDimensionValues={props.isLoadingDimensionValues}
          onInteraction={props.onInteraction}
        />
      </Flex>
      <Box marginBottom={theme.space_sm}>
        <Divider color={"#C2C8D0"} direction="vertical" minHeight={24} />
      </Box>
      <Flex direction="column" gap={theme.space_xs}>
        <Box>
          <Flex alignItems="center" gap={theme.space_xs}>
            <Text fontSize="18px">{copyText.costDestinationSectionTitle}</Text>
            <Tooltip
              content={copyText.costDestinationTooltipMessage}
              placement="bottom"
              width="250px"
            >
              <Icon color={"#535862"} icon={faInfoCircle} />
            </Tooltip>
          </Flex>
          <Text color="#7C8591">
            {copyText.costDestinationSectionDescription}
          </Text>
        </Box>
        <Box
          backgroundColor={theme.panel_backgroundColor}
          borderRadius={theme.borderRadius_2}
        >
          <Box
            borderBottom={`1px solid ${theme.border_color}`}
            padding={theme.space_md}
          >
            <form.Field
              name="reallocationType"
              validators={{
                onChange: z
                  .nativeEnum(ReallocationType)
                  .refine((value) => value !== null),
                onMount: z
                  .nativeEnum(ReallocationType)
                  .refine((value) => value !== null),
              }}
              children={(field) => {
                const selectedOption = reallocationTypeOptions.find(
                  (option) => option.value === field.state.value
                );

                return (
                  <FormField
                    label={copyText.typeInputLabel}
                    marginBottom={0}
                    required
                  >
                    <Select
                      options={reallocationTypeOptions}
                      value={selectedOption}
                      onChange={(option) => {
                        if (!option) return;

                        if (option.value === ReallocationType.STATIC) {
                          form.setFieldValue("targetsStatic", [
                            {
                              identifier: {
                                labels: [{ key: "", value: "" }],
                                subAccountID: "",
                              },
                              share: 0,
                            },
                          ]);
                        }

                        field.handleChange(option.value);
                      }}
                    />
                  </FormField>
                );
              }}
            />
          </Box>
          {formState.reallocationType === ReallocationType.DYNAMIC &&
            renderDynamicReallocationContent()}
          {formState.reallocationType === ReallocationType.STATIC &&
            renderStaticReallocationContent()}
        </Box>
      </Flex>
    </>
  );
}
